import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import { EMPTY_VALUE } from '@app/dashboard/constants/shared';
import { CUSTOMER_TAPS } from '@app/dashboard/constants/transactions';
import { TabsInfoCard } from '@app/dashboard/components/inner_layout/Cards/TabsInfoCard';
import { InfoCard } from '@app/dashboard/components/inner_layout/Cards/InfoCard';

import { getCustomerNameFields } from '../../utils';

const TABS_ALIGNMENT_BREAKPOINT = 1455;

const CustomerDetails = ({
  customerInfo,
}: {
  customerInfo: {
    customer_details: Record<string, any>;
    billing_info: Record<string, any>;
    shipping_details: Record<string, any>;
  };
}) => {
  const { t } = useTranslation();
  const screenWidth = useScreenWidth();

  const [activeCustomerTab, setActiveCustomerTab] = useState<string>(CUSTOMER_TAPS.CUSTOMER);

  const { customer_details, billing_info, shipping_details } = customerInfo || {};
  const hasBillingAddress = !!billing_info;
  const hasShippingAddress = !!shipping_details;
  const showCustomerTabs = hasBillingAddress || hasShippingAddress;
  const isTabsVertical = screenWidth <= TABS_ALIGNMENT_BREAKPOINT;

  const getBillingAddressPairs = () => {
    if (!hasBillingAddress) return [];

    return [
      {
        id: CUSTOMER_TAPS.BILLING,
        labelKey: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.billing'),
      },
    ];
  };

  const getShippingAddressPairs = () => {
    if (!hasShippingAddress) return [];

    return [
      {
        id: CUSTOMER_TAPS.SHIPPING,
        labelKey: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.shipping'),
      },
    ];
  };

  const customerTabs = showCustomerTabs
    ? [
        {
          id: CUSTOMER_TAPS.CUSTOMER,
          labelKey: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.customer'),
        },
        ...getBillingAddressPairs(),
        ...getShippingAddressPairs(),
      ]
    : [];

  const customerDetails = [
    ...getCustomerNameFields(customer_details || {}),
    {
      label: t('form.email'),
      value: customer_details?.email || EMPTY_VALUE,
    },
    {
      label: t('form.phoneNumber'),
      value: customer_details?.phone_number || EMPTY_VALUE,
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.customerAddress'),
      value: customer_details?.address || EMPTY_VALUE,
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.country'),
      value: customer_details?.country || EMPTY_VALUE,
    },
  ];

  const billingDetails = [
    ...getCustomerNameFields(billing_info || {}),
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.email'),
      value: billing_info?.email || EMPTY_VALUE,
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.phoneNumber'),
      value: billing_info?.phone_number || EMPTY_VALUE,
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.country'),
      value: billing_info?.country || EMPTY_VALUE,
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.state'),
      value: billing_info?.state || EMPTY_VALUE,
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.city'),
      value: billing_info?.city || EMPTY_VALUE,
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.postalCode'),
      value: billing_info?.postal_code || EMPTY_VALUE,
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.addressLine1'),
      value: billing_info?.address_line_1 || EMPTY_VALUE,
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.addressLine2'),
      value: billing_info?.address_line_2 || EMPTY_VALUE,
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.building'),
      value: billing_info?.building || EMPTY_VALUE,
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.floor'),
      value: billing_info?.floor || EMPTY_VALUE,
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.apartment'),
      value: billing_info?.apartment || EMPTY_VALUE,
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.street'),
      value: billing_info?.street || EMPTY_VALUE,
    },
  ];

  const shippingDetails = [
    ...getCustomerNameFields(shipping_details || {}),
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.email'),
      value: shipping_details?.email || EMPTY_VALUE,
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.phoneNumber'),
      value: shipping_details?.phone_number || EMPTY_VALUE,
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.country'),
      value: shipping_details?.country || EMPTY_VALUE,
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.state'),
      value: shipping_details?.state || EMPTY_VALUE,
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.city'),
      value: shipping_details?.city || EMPTY_VALUE,
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.postalCode'),
      value: shipping_details?.postal_code || EMPTY_VALUE,
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.addressLine1'),
      value: shipping_details?.address_line_1 || EMPTY_VALUE,
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.addressLine2'),
      value: shipping_details?.address_line_2 || EMPTY_VALUE,
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.building'),
      value: shipping_details?.building || EMPTY_VALUE,
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.floor'),
      value: shipping_details?.floor || EMPTY_VALUE,
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.apartment'),
      value: shipping_details?.apartment || EMPTY_VALUE,
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.street'),
      value: shipping_details?.street || EMPTY_VALUE,
    },
  ];

  const customerDetailsWithTab: Record<string, { label: string; value: string }[]> = {
    [CUSTOMER_TAPS.CUSTOMER]: customerDetails,
    [CUSTOMER_TAPS.BILLING]: billingDetails,
    [CUSTOMER_TAPS.SHIPPING]: shippingDetails,
  };

  const memoizedCustomerTabs = useMemo(() => {
    return customerTabs.map((tab) => ({
      id: tab.id,
      text: tab.labelKey,
      active: activeCustomerTab === tab.id,
      onClickHandler: () => setActiveCustomerTab(tab.id),
    }));
  }, [activeCustomerTab, customerTabs]);

  return showCustomerTabs ? (
    <TabsInfoCard
      title={t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.title')}
      alignment={isTabsVertical ? 'vertical' : 'horizontal'}
      tabs={memoizedCustomerTabs}
      noOfColumns={CUSTOMER_TAPS.CUSTOMER === activeCustomerTab ? 1 : 2}
      pairsList={customerDetailsWithTab[activeCustomerTab]}
    />
  ) : (
    <InfoCard
      title={t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.title')}
      pairsList={customerDetails}
      noOfColumns={1}
    />
  );
};

export default CustomerDetails;
